import type { SharedModels } from '@boostbrothers/web-api';
import {
  ClientApiFactory,
  type CreateContentReqModel,
  type CreateContentResModel,
  type PatchContentReqModel,
} from '@boostbrothers/web-api/web-gateway-api';
import { SimpleAlertModal } from '@boostbrothers/web-ui/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PA_URL } from '~constants/url';
import useModal from '~hooks/useModal';
import useToast from '~hooks/useToast';
import useViewTransition from '~hooks/useViewTransition';

const useContentMutation = ({
  fromPA,
  handleTemporarySave,
}: {
  fromPA: boolean;
  handleTemporarySave: () => void;
}) => {
  const queryClient = useQueryClient();
  const { navigateWithTransition } = useViewTransition();
  const { addToast } = useToast();
  const { openModal, closeModal } = useModal();

  const handleError = (error: SharedModels.ErrorRes['error']) => {
    if (error.status === 401) {
      openModal({
        modalContent: fromPA ? (
          <SimpleAlertModal
            title="로그인 세션이 만료되었습니다. PA에서 다시 진입해주세요."
            description={`해당 페이지는 닫히지 않습니다. 필요한 내용을 복사 후, 새로운 탭에서 이어서 작성해주세요.`}
            confirmText="로그인"
            confirmAction={() => {
              closeModal();
              sessionStorage.removeItem('userToken');

              window.open(PA_URL, '_blank');
            }}
          />
        ) : (
          <SimpleAlertModal
            title="로그인 세션이 만료되었습니다."
            description="다시 로그인 하시면 현재 작성된 내용 그대로 이어서 작성할 수 있습니다."
            confirmText="로그인"
            confirmAction={() => {
              closeModal();
              localStorage.removeItem('userToken');
              handleTemporarySave();
              navigateWithTransition('/login');
            }}
          />
        ),
      });
      return;
    }

    addToast({
      title: error.message || '저장을 실패했습니다.',
      state: 'error',
      position: 'bottom-center',
    });
  };

  const { mutateAsync: mutateUpdateContent } = useMutation<
    void,
    SharedModels.ErrorRes,
    PatchContentReqModel
  >({
    mutationFn: payload => {
      return ClientApiFactory('PATCH /v1/cms/contents/:contentId')(payload);
    },
    onSuccess: async (_, { params }) => {
      addToast({
        title: '작성하신 내용이 저장되었습니다.',
        state: 'success',
        position: 'bottom-center',
      });

      if (params?.contentId) {
        await queryClient.invalidateQueries({
          queryKey: ['content', params?.contentId],
        });
      }
    },
    onError: ({ error }) => handleError(error),
  });

  const { mutateAsync: mutateCreateContent } = useMutation<
    CreateContentResModel,
    SharedModels.ErrorRes,
    CreateContentReqModel
  >({
    mutationFn: body => {
      return ClientApiFactory('POST /v1/cms/contents')(body);
    },
    onSuccess: res => {
      addToast({
        title: '작성하신 내용이 저장되었습니다.',
        state: 'success',
        position: 'bottom-center',
      });
      navigateWithTransition(`/edit?contentId=${res._id}`);
    },
    onError: ({ error }) => handleError(error),
  });

  return {
    mutateUpdateContent,
    mutateCreateContent,
  };
};

export default useContentMutation;
